<template>
  <Modal
    :title="modalTitle"
    :saving="saving"
    :disabled="isPendingAppt"
    @submit="handleSubmit"
    @hide-modal="$emit('hide-modal')"
  >
    <div>
      <h4 v-if="false" class="mb-2">Technician</h4>
      <Card headless class="mb-3">
        <h5 class="mb-2 font-weight-bold">Technician</h5>
        <h5 v-if="modalTechnician" class="mb-2">
          <b class="mr-1">
            <a
              :target="'_blank'"
              :href="`/technicians/${modalTechnician.user_id}`"
            >
              {{ fullName(modalTechnician) }}
            </a>
          </b>
          <a target="_blank" :href="`/technicians/${modalTechnician.user_id}`">
            <i class="fa-regular fa-arrow-up-right-from-square ml-1"></i
          ></a>
        </h5>
        <component
          :is="actualTimesFormVersion"
          :appt-state="appointment.status"
          :accepted-time="formData.accepted_time"
          :dispatch-time="formData.dispatch_time"
          :arrival-time="formData.arrival_time"
          :departure-time="formData.departure_time"
          :scheduled-time="appointment.scheduled_time"
          :disabled-fields="disabledTimeFields()"
          @change="handleTimeChange"
        >
          <template #label>
            <permission-check
              v-if="isPendingAppt"
              class="mb-3"
              :permissions="['dispatch']"
            >
              <p class="mb-4">
                The actual times for a pending appointment can't be adjusted. Do
                you need to
                <a
                  class="view-on-schedule"
                  :href="`/?appointment_id=${
                    appointment.id
                  }&start=${parseToDate(appointment.scheduled_time)}`"
                >
                  edit the appointment status</a
                >?
              </p>
            </permission-check>
          </template>
        </component>
      </Card>
      <h4 class="d-flex mb-2">Crew Members</h4>
      <Card headless>
        <div v-if="isPendingAppt">
          The crew members for a pending appointment cannot be adjusted.
        </div>
        <div v-else>
          <p v-if="visibleCrewMembers.length">
            By default, crew members will use the same actual times as the
            technician on the appointment. Edit a crew member’s individual time
            by unchecking the “Use Tech’s Time” box and editing the actual time
            fields below.
          </p>
          <div class="crew-members-display">
            <crew-times-row
              v-for="member in visibleCrewMembers"
              :key="`${member.id}-${member.index}`"
              :crew-member="member"
              :appointment="appointment"
              @remove-crew-member="handleRemoveCrew"
              @change="handleCrewMemberUpdate"
            />
          </div>
          <eligible-crew-form
            :fetch-link="appointment?.links?.crew_member_list"
            :crew-members="appointment.crew_members"
            class="mb-3"
            @add-crew="handleAddingMember"
          />
        </div>
      </Card>
      <Card v-if="!isPendingAppt" headless class="mt-4">
        <TextArea
          v-model="formData.reason"
          class="full-width mb-2"
          label="Reason for Adjusting Times"
          name="Reason"
          :rows="3"
          :validation-errors="v$.$errors"
        />
        <change-summary
          :changes="changes"
          :original="originalValues"
        ></change-summary>
      </Card>
    </div>
  </Modal>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import {
  buildTechniciansById,
  getObjDiff,
  normalizeId
} from "@/lib/helpers.js";

import Card from "@/components/Card.vue";
import Modal from "@/components/Modal.vue";
import PermissionCheck from "@/components/PermissionCheck.vue";
import TextArea from "@/components/TextArea.vue";
import CONSTANTS from "@/Constants.js";
import ChangeSummary from "@/EditAppointmentModal/ChangeSummary.vue";
import ActualTimesFormV2 from "@/EditAppointmentModal/forms/ActualTimesFormV2.vue";
import CrewTimesRow from "@/Job/CrewTimesRow.vue";
import EligibleCrewForm from "@/Job/EligibleCrewForm.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
const { APPOINTMENT_STATUS_REQUIRED_TIMES } = CONSTANTS;

export default {
  components: {
    Modal,
    EligibleCrewForm,
    TextArea,
    Card,
    CrewTimesRow,
    ActualTimesFormV2,
    ChangeSummary,
    PermissionCheck
  },
  props: {
    appointment: {
      type: Object,
      default: () => ({})
    },
    technician_profile_id: {
      type: [Number, String],
      default: null
    }
  },
  emits: ["hide-modal"],
  data() {
    return {
      formData: {
        accepted_time: this.appointment.accepted_time,
        dispatch_time: this.appointment.dispatch_time,
        arrival_time: this.appointment.arrival_time,
        departure_time: this.appointment.departure_time,
        crew_members: [],
        reason: ""
      },
      saving: false,
      nextIndex: 0
    };
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant"]),
    isMultiTechEnabled() {
      return this.featureEnabledForTenant("multiple_technicians_ff_enabled");
    },
    normalizedTechnicianProfileId() {
      return normalizeId(this.technician_profile_id);
    },
    techniciansById() {
      return buildTechniciansById(this.appointment);
    },
    modalTechnician() {
      if (!this.isMultiTechEnabled) {
        return this.appointment.technician;
      }

      if (this.normalizedTechnicianProfileId == null) {
        return this.appointment.technician;
      }

      return (
        this.techniciansById.get(this.normalizedTechnicianProfileId) ||
        this.appointment.technician
      );
    },
    appointmentCrewMembers() {
      return (this.appointment.crew_members || []).map((member, i) => ({
        ...member,
        index: i
      }));
    },
    visibleCrewMembers() {
      if (!this.isMultiTechEnabled) {
        return this.formData.crew_members || [];
      }

      if (this.normalizedTechnicianProfileId == null) {
        return this.formData.crew_members || [];
      }

      return (this.formData.crew_members || []).filter((member) => {
        const assignedToId = normalizeId(member?.assigned_to?.technician_id);
        const crewLeaderId = normalizeId(member?.crew_leader_id);
        return (
          assignedToId === this.normalizedTechnicianProfileId ||
          crewLeaderId === this.normalizedTechnicianProfileId
        );
      });
    },
    modalTitle() {
      return `Edit Times & Crew Members for Appointment #${this.appointment.id}`;
    },
    changes() {
      const changes = getObjDiff(this.originalValues, this.formData);
      // Use full list of crew_members as change instead of just new ones
      // so the change summary message can be built correctly
      return {
        ...changes,
        crew_members: this.formData.crew_members
      };
    },
    originalValues() {
      let source = this.appointment;
      if (
        this.isMultiTechEnabled &&
        this.normalizedTechnicianProfileId != null
      ) {
        const appointmentTechnician = (
          this.appointment.appointment_technicians || []
        ).find(
          (r) =>
            normalizeId(r.technician_profile_id) ===
            this.normalizedTechnicianProfileId
        );
        if (appointmentTechnician) source = appointmentTechnician;
      }
      const { accepted_time, arrival_time, dispatch_time, departure_time } =
        source;
      return {
        accepted_time,
        arrival_time,
        dispatch_time,
        departure_time,
        crew_members: [...this.appointmentCrewMembers],
        travel: "",
        work: "",
        duration: ""
      };
    },
    isPendingAppt() {
      return this.appointment.status === "pending";
    },
    actualTimesFormVersion() {
      return ActualTimesFormV2;
    }
  },
  created() {
    this.formData.crew_members = [...this.appointmentCrewMembers];
    this.nextIndex = this.formData.crew_members.length;
    if (this.isMultiTechEnabled && this.normalizedTechnicianProfileId != null) {
      const appointmentTechnician = (
        this.appointment.appointment_technicians || []
      ).find(
        (r) =>
          normalizeId(r.technician_profile_id) ===
          this.normalizedTechnicianProfileId
      );
      if (appointmentTechnician) {
        this.formData.accepted_time = appointmentTechnician.accepted_time;
        this.formData.dispatch_time = appointmentTechnician.dispatch_time;
        this.formData.arrival_time = appointmentTechnician.arrival_time;
        this.formData.departure_time = appointmentTechnician.departure_time;
      }
    }
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      formData: {
        reason: { required, $autoDirty: true }
      }
    };
  },
  methods: {
    ...mapActions(["updateAppointmentTimes"]),
    handleAddingMember(member) {
      // when adding a new crew member pre-save
      const crewLeaderId = this.isMultiTechEnabled
        ? this.normalizedTechnicianProfileId
        : null;
      this.formData.crew_members = [
        ...this.formData.crew_members,
        {
          ...member,
          crew_leader_id: crewLeaderId,
          use_crew_leader_times: true,
          index: this.nextIndex
        }
      ];
      this.nextIndex = this.nextIndex + 1;
    },
    handleRemoveCrew(removeMember) {
      this.formData.crew_members = this.formData.crew_members.filter(
        (member) =>
          !(
            member.index === removeMember.index && member.id === removeMember.id
          )
      );
    },
    handleSubmit() {
      this.v$.$validate().then((valid) => {
        if (valid) {
          this.saving = true;
          const { crewMembersPayload, validationError } =
            this.buildCrewMembersPayload();

          if (validationError) {
            this.saving = false;
            window.notify.error(validationError);
            return;
          }

          const payload = {
            id: this.appointment.id,
            ...this.formData,
            crew_members: crewMembersPayload
          };
          if (
            this.isMultiTechEnabled &&
            this.normalizedTechnicianProfileId != null
          ) {
            payload.technician_profile_id = this.normalizedTechnicianProfileId;
          }
          this.updateAppointmentTimes(payload)
            .then(() => {
              window.notify.success("Appointment updated successfully!");
              this.$emit("hide-modal");
            })
            .finally(() => (this.saving = false));
        }
      });
    },
    disabledTimeFields() {
      let keys;
      if (this.appointment.status == "cancelled") {
        // disable all _time fields if appt is cancelled
        keys = Object.keys(this.formData).filter((field) => {
          return field.endsWith("_time");
        });
      } else {
        // If the _time is missing from the Appointment properties and it is not
        // required based on APPOINTMENT_STATUS_REQUIRED_TIMES, disable it.
        keys = Object.keys(this.formData).filter((field) => {
          const requiredTimes =
            APPOINTMENT_STATUS_REQUIRED_TIMES[this.appointment.status] || [];
          return !this.appointment[field] && !requiredTimes.includes(field);
        });
      }

      return keys;
    },
    handleTimeChange(name, value) {
      this.formData[name] = value;
    },
    handleCrewMemberUpdate(crewMember) {
      this.formData.crew_members = (this.formData.crew_members || []).map(
        (member) => {
          if (
            member.index === crewMember.index &&
            member.id === crewMember.id
          ) {
            return {
              ...crewMember
            };
          }
          return member;
        }
      );
    },
    hasCompleteOverrideTimes(crew) {
      return Boolean(
        crew.dispatch_time && crew.arrival_time && crew.departure_time
      );
    },
    isCrewMemberVisibleForCurrentModal(crew) {
      if (
        !this.isMultiTechEnabled ||
        this.normalizedTechnicianProfileId == null
      ) {
        return true;
      }
      const assignedToId = normalizeId(crew?.assigned_to?.technician_id);
      const crewLeaderId = normalizeId(crew?.crew_leader_id);
      return (
        assignedToId === this.normalizedTechnicianProfileId ||
        crewLeaderId === this.normalizedTechnicianProfileId
      );
    },
    buildCrewMembersPayload() {
      const crewMembersPayload = [];
      for (const crew of this.formData.crew_members || []) {
        const {
          id,
          accepted_time,
          departure_time,
          arrival_time,
          dispatch_time,
          use_crew_leader_times
        } = crew;

        let payload = { id };

        if (!use_crew_leader_times) {
          if (!this.hasCompleteOverrideTimes(crew)) {
            if (this.isCrewMemberVisibleForCurrentModal(crew)) {
              return {
                crewMembersPayload: [],
                validationError:
                  "You must set Dispatch, Arrival, and Departure for crew members with Use Tech's Time unchecked."
              };
            }
          } else {
            payload = {
              ...payload,
              accepted_time,
              departure_time,
              arrival_time,
              dispatch_time
            };
          }
        }

        crewMembersPayload.push(payload);
      }

      return {
        crewMembersPayload,
        validationError: null
      };
    },
    parseToDate(ISODate) {
      return moment(ISODate).format(CONSTANTS.DATE_YEAR_CAL_FORMAT);
    }
  }
};
</script>

<style lang="scss" scoped>
.crew-members-display {
  display: flex;
  flex-direction: column;
}
</style>
